import { Link } from 'react-router-dom';
import { CollectionPanel } from '../components/CollectionPanel';
import { Distribution } from '../components/Distribution';
import { EventRow } from '../components/EventRow';
import { JobCard } from '../components/JobCard';
import { MomentumBreakdown } from '../components/MomentumBreakdown';
import { PageHeader } from '../components/PageHeader';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel';
import { useDashboard } from '../hooks/useRadarQuery';

export function DashboardPage() {
  const query = useDashboard();
  if (query.isLoading) return <LoadingPanel />;
  if (query.isError || !query.data) return <ErrorPanel error={query.error} />;
  const data = query.data;
  const stats = [
    ['Tracked companies', data.counts.companies],
    ['Verified profiles', data.counts.verifiedCompanies],
    ['Open roles', data.counts.openJobs],
    ['Stealth leads', data.counts.researchCandidates],
    ['Signals · 30d', data.counts.events30d],
  ];
  return (
    <>
      <PageHeader
        eyebrow="US AI-for-science intelligence"
        title="Models meet the real world."
        description="Track the companies learning, designing, and controlling physical, biological, and chemical systems—with evidence, timelines, and live hiring signals."
        actions={<Link className="button" to="/companies">Explore companies</Link>}
      />
      <section className="stats-grid">
        {stats.map(([name, value]) => <article className="stat-card" key={name}><strong>{value}</strong><span>{name}</span></article>)}
      </section>
      <section className="dashboard-grid">
        <div className="panel panel--wide">
          <div className="panel__heading"><div><span className="eyebrow">Timeline</span><h2>Recent signals</h2></div><Link to="/events">View all</Link></div>
          {data.recentEvents.length ? data.recentEvents.map((event) => <EventRow key={event.id} event={event} />) : <EmptyPanel message="No observed events yet." />}
        </div>
        <div className="panel">
          <div className="panel__heading"><div><span className="eyebrow">Momentum</span><h2>Evidence-weighted leaders</h2></div></div>
          <div className="momentum-list">
            {data.momentum.length ? data.momentum.map((item, index) => (
              <div className="momentum-item" key={item.companyId}>
                <span className="momentum-item__rank">{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <strong>{item.companyName}</strong>
                  <div className="progress"><span style={{ width: `${item.overall}%` }} /></div>
                  <MomentumBreakdown score={item} />
                </div>
                <b>{item.overall}</b>
              </div>
            )) : <EmptyPanel message="Add reviewed events to calculate momentum." />}
          </div>
        </div>
      </section>
      {/* Placed above composition on purpose. Everything below this point is a
          reading of collected data, and this is the panel that says whether the
          collection behind it is still happening — a stalled feed leaves every
          number under it looking current. It fetches on its own so a slow or
          empty registry projection cannot take the answer down with it. */}
      <section className="section-gap"><CollectionPanel /></section>
      {/* Composition was computed and served from the first release and never
          shown, so the dashboard paid two queries a load to send nothing. What
          the registry is weighted toward is the question a reader asks first of
          a curated set, and the answer was already in the response. */}
      <section className="dashboard-grid section-gap">
        <div className="panel">
          <div className="panel__heading"><div><span className="eyebrow">Composition</span><h2>Domains tracked</h2></div><Link to="/companies">Filter</Link></div>
          {data.domains.length
            ? <Distribution items={data.domains.map((item) => ({ name: item.domain, count: item.count }))} />
            : <EmptyPanel message="No companies classified yet." />}
        </div>
        <div className="panel">
          <div className="panel__heading"><div><span className="eyebrow">Composition</span><h2>Funding stages</h2></div></div>
          {data.stages.length
            ? <Distribution items={data.stages.map((item) => ({ name: item.stage, count: item.count }))} />
            : <EmptyPanel message="No stages recorded yet." />}
        </div>
      </section>
      <section className="panel section-gap">
        <div className="panel__heading"><div><span className="eyebrow">Opportunity radar</span><h2>New roles</h2></div><Link to="/jobs">View all jobs</Link></div>
        <div className="card-grid">{data.recentJobs.length ? data.recentJobs.slice(0, 6).map((job) => <JobCard key={job.id} job={job} />) : <EmptyPanel message="Configure ATS boards to populate current roles." />}</div>
      </section>
    </>
  );
}
